import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '../hooks/usePermissions';
import { useOrderWebSocket } from '../hooks/useOrderWebSocket';
import { OrdersService, type PedidoResponse } from '../services/orders/orders.service';
import { ChatService } from '../services/chat/chat.service';
import { webSocketService } from '../services/websocket/websocket.service';
import toast from 'react-hot-toast';
import { useUnreadMessagesStore } from '../store/unread-messages.store';
import { OrderDetailsDrawer } from '../components/orders/OrderDetailsDrawer';
import { DatePickerInput } from '../components/ui/DatePickerInput';
import { getColombiaDate, toColombiaDateString } from '../utils/date';
import { 
  Package, 
  Search, 
  Calendar,
  CreditCard,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  ShoppingBag,
  DollarSign,
  MessageCircle,
  User,
  Banknote,
  Smartphone,
  Tag,
  CalendarDays
} from 'lucide-react';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

interface PaginatedOrdersResponse {
  success: boolean;
  data: PedidoResponse[];
  meta: PaginationMeta;
}

const metodosPagoMap: Record<string, { label: string; icono: React.ReactNode; bg: string; color: string }> = {
  'EFECTIVO': { label: 'Efectivo', icono: <Banknote className="w-3.5 h-3.5" />, bg: 'bg-green-100', color: 'text-green-700' },
  'TARJETA': { label: 'Tarjeta', icono: <CreditCard className="w-3.5 h-3.5" />, bg: 'bg-purple-100', color: 'text-purple-700' },
  'CARD': { label: 'Tarjeta', icono: <CreditCard className="w-3.5 h-3.5" />, bg: 'bg-purple-100', color: 'text-purple-700' },
  'NEQUI': { label: 'Nequi', icono: <Smartphone className="w-3.5 h-3.5" />, bg: 'bg-blue-100', color: 'text-blue-700' },
  'DAVIPLATA': { label: 'Daviplata', icono: <Smartphone className="w-3.5 h-3.5" />, bg: 'bg-indigo-100', color: 'text-indigo-700' },
  'TRANSFERENCIA': { label: 'Transferencia', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-amber-100', color: 'text-amber-700' },
  'PSE': { label: 'PSE', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-amber-100', color: 'text-amber-700' },
  'BANCOLOMBIA_TRANSFER': { label: 'Bancolombia', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-amber-100', color: 'text-amber-700' },
  'EFECTY': { label: 'Efecty', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-orange-100', color: 'text-orange-700' },
};

const estadosPedido: Record<string, { 
  label: string; 
  color: string; 
  bg: string;
  icono: React.ReactNode;
}> = {
  'PENDING_PAYMENT': { 
    label: 'Pendiente', 
    color: 'text-yellow-700',
    bg: 'bg-yellow-100',
    icono: <Clock className="w-4 h-4" /> 
  },
  'PAID': { 
    label: 'Pagado', 
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    icono: <CreditCard className="w-4 h-4" /> 
  },
  'PROCESSING': { 
    label: 'En prep.', 
    color: 'text-purple-700',
    bg: 'bg-purple-100',
    icono: <Package className="w-4 h-4" /> 
  },
  'SHIPPED': { 
    label: 'En camino', 
    color: 'text-indigo-700',
    bg: 'bg-indigo-100',
    icono: <Truck className="w-4 h-4" /> 
  },
  'DELIVERED': { 
    label: 'Entregado', 
    color: 'text-green-700',
    bg: 'bg-green-100',
    icono: <CheckCircle className="w-4 h-4" /> 
  },
  'CANCELLED': { 
    label: 'Cancelado', 
    color: 'text-red-700',
    bg: 'bg-red-100',
    icono: <XCircle className="w-4 h-4" /> 
  },
};

// Componente de tabla de ventas
interface OrdersTableProps {
  orders: PedidoResponse[];
  onOrderClick: (order: PedidoResponse) => void;
  isLoading?: boolean;
  mensajesNoLeidos: Record<string, number>;
}

const OrdersTable: React.FC<OrdersTableProps> = ({ orders, onOrderClick, isLoading, mensajesNoLeidos }) => {
  const { isSuperAdmin } = usePermissions();

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('es-CO')}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>No se encontraron ventas</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Pedido
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Cliente
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Vendedor
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Fecha
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Estado
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
              Método
            </th>
            {isSuperAdmin() && (
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                Margen
              </th>
            )}
            <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">
              Total
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {orders.map((pedido) => {
            const estado = estadosPedido[pedido.estado];
            return (
              <tr 
                key={pedido.id}
                onClick={() => onOrderClick(pedido)}
                className="cursor-pointer transition-colors hover:bg-emerald-50/50"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-100">
                      <Package className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">
                          {pedido.numeroPedido}
                        </p>
                        {/* Indicador de mensajes no leídos */}
                        {(mensajesNoLeidos[pedido.id] || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-medium animate-pulse">
                            <MessageCircle className="w-3 h-3" />
                            {mensajesNoLeidos[pedido.id]}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {pedido.items.length} productos
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-gray-900">
                      {pedido.cliente.nombre}
                    </p>
                    <p className="text-sm text-gray-500">
                      {pedido.cliente.email}
                    </p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">
                      {pedido.vendedor?.nombre || '—'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">
                      {formatFecha(pedido.fechas.creadoEn)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${estado?.bg} ${estado?.color}`}>
                    {estado?.icono}
                    {estado?.label || pedido.estado}
                  </span>
                </td>
                <td className="px-6 py-4">
                  {(() => {
                    if (pedido.pagos && pedido.pagos.length > 1) {
                      return (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-cyan-100 text-cyan-700">
                          <DollarSign className="w-3.5 h-3.5" />
                          Pago mixto
                        </span>
                      );
                    }
                    const mp = metodosPagoMap[pedido.metodoPago || ''] || { label: pedido.metodoPago || '—', icono: <Tag className="w-3.5 h-3.5" />, bg: 'bg-gray-100', color: 'text-gray-700' };
                    return (
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${mp.bg} ${mp.color}`}>
                        {mp.icono}
                        {mp.label}
                      </span>
                    );
                  })()}
                </td>
                {isSuperAdmin() && (
                  <td className="px-6 py-4">
                    {(() => {
                      const costoTotal = pedido.items.reduce((sum, item) => sum + (item.precioCompra || 0) * item.cantidad, 0);
                      const margen = costoTotal > 0 ? ((pedido.totales.subtotal - costoTotal) / costoTotal) * 100 : 0;
                      const ganancia = pedido.totales.subtotal - costoTotal;
                      return costoTotal > 0 ? (
                        <div className="flex flex-col">
                          <span className={`text-sm font-semibold ${ganancia >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {margen.toFixed(1)}%
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatCurrency(ganancia)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      );
                    })()}
                  </td>
                )}
                <td className="px-6 py-4 text-right">
                  <p className="font-bold text-lg text-gray-900">
                    {formatCurrency(pedido.totales.total)}
                  </p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const OrdersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedOrder, setSelectedOrder] = useState<PedidoResponse | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('PAID');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'custom'>('today');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { 
    mensajesNoLeidosPorPedido, 
    setNoLeidosPorPedido, 
    incrementarNoLeidos, 
    limpiarNoLeidos 
  } = useUnreadMessagesStore();
  const isDrawerOpen = selectedOrder !== null;

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Calcular fechas según el rango seleccionado (en zona horaria Colombia)
  useEffect(() => {
    const hoy = getColombiaDate();
    const hoyStr = toColombiaDateString(hoy);

    if (dateRange === 'today') {
      setFechaInicio(hoyStr);
      setFechaFin(hoyStr);
    } else if (dateRange === 'week') {
      const inicioSemana = new Date(hoy);
      inicioSemana.setDate(hoy.getDate() - 6);
      setFechaInicio(toColombiaDateString(inicioSemana));
      setFechaFin(hoyStr);
    } else if (dateRange === 'month') {
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      setFechaInicio(toColombiaDateString(inicioMes));
      setFechaFin(hoyStr);
    } else if (dateRange === 'custom') {
      // No tocar las fechas personalizadas
    }
  }, [dateRange]);

  // Resetear página cuando cambie el término de búsqueda o filtros
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, filtroEstado, fechaInicio, fechaFin]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Query para obtener ventas
  const { isSuperAdmin } = usePermissions();

  const { 
    data, 
    isLoading, 
    error,
    refetch,
    isFetching
  } = useQuery<PaginatedOrdersResponse>({
    queryKey: ['orders', page, limit, debouncedSearchTerm, filtroEstado, fechaInicio, fechaFin],
    queryFn: async () => {
      const response = await OrdersService.list(
        page, 
        limit, 
        filtroEstado || undefined, 
        debouncedSearchTerm || undefined,
        fechaInicio || undefined,
        fechaFin || undefined,
      );
      return response as PaginatedOrdersResponse;
    },
    staleTime: 60000,
  });

  const orders = data?.data || [];
  const paginationMeta = data?.meta;

  // Query de resumen global (todas las páginas)
  const { data: summaryData } = useQuery({
    queryKey: ['orders-summary', debouncedSearchTerm, filtroEstado, fechaInicio, fechaFin],
    queryFn: async () => {
      const response = await OrdersService.getSummary(
        filtroEstado || undefined,
        debouncedSearchTerm || undefined,
        fechaInicio || undefined,
        fechaFin || undefined,
      );
      return response.data;
    },
    staleTime: 60000,
  });

  // Consultar mensajes no leídos para las ventas visibles
  useEffect(() => {
    const fetchMensajesNoLeidos = async () => {
      if (!orders || orders.length === 0) return;
      
      const pedidoIds = orders.map(o => o.id);
      const response = await ChatService.obtenerNoLeidosBatch(pedidoIds);
      
      if (response.success && response.data) {
        setNoLeidosPorPedido(response.data);
      }
    };

    fetchMensajesNoLeidos();
    
    // No se usa polling; los mensajes se actualizan vía WebSocket
  }, [orders, setNoLeidosPorPedido]);

  // Escuchar mensajes nuevos vía WebSocket
  useEffect(() => {
    const handleChatMessage = (payload: any) => {
      const { pedidoId, mensaje } = payload;
      // Solo incrementar si el mensaje es del cliente (no del admin)
      if (mensaje.remitente === 'CLIENTE') {
        incrementarNoLeidos(pedidoId);
      }
    };

    (webSocketService as any).addEventListener('order_chat_message', handleChatMessage);
    return () => {
      (webSocketService as any).removeEventListener('order_chat_message', handleChatMessage);
    };
  }, [incrementarNoLeidos]);

  // Limpiar mensajes no leídos cuando se abre un pedido
  const handleOrderClick = (order: PedidoResponse) => {
    setSelectedOrder(order);
    // Limpiar contador de mensajes no leídos para este pedido
    limpiarNoLeidos(order.id);
  };

  // WebSocket para actualizaciones en tiempo real
  useOrderWebSocket({
    onNewOrder: () => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['orders-summary'] });
    },
    onStatusChange: (event) => {
      refetch();
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['orders-summary'] });
    },
  });

  // Mantener foco en el input después de la búsqueda
  useEffect(() => {
    if (!isFetching && searchTerm && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isFetching, searchTerm]);

  const handleCloseDrawer = () => {
    setSelectedOrder(null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePreviousPage = () => {
    if (paginationMeta?.hasPrevious) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMeta?.hasNext) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('es-CO')}`;
  };

  // Estadísticas (del resumen global, no solo la página actual)
  const stats = {
    total: summaryData?.total ?? paginationMeta?.total ?? 0,
    pendientes: summaryData?.pendientes ?? 0,
    procesando: summaryData?.procesando ?? 0,
    entregados: summaryData?.entregados ?? 0,
    totalVentas: summaryData?.totalVentas ?? 0,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error al cargar las ventas</div>
          <button 
            onClick={() => refetch()}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="px-6 pt-6 pb-2">
        <h1 className="text-2xl font-semibold text-gray-900">
          Gestión de Ventas
        </h1>
        <p className="mt-1 text-gray-600">
          Administra y da seguimiento a todas las ventas
        </p>
      </div>

      {/* Stats Cards */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="p-4 rounded-xl shadow-sm bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Ventas</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
                <ShoppingBag className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl shadow-sm bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pendientes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendientes}</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-100 text-yellow-600">
                <Clock className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl shadow-sm bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">En Proceso</p>
                <p className="text-2xl font-bold text-gray-900">{stats.procesando}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
                <Package className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl shadow-sm bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entregados</p>
                <p className="text-2xl font-bold text-gray-900">{stats.entregados}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-100 text-green-600">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-xl shadow-sm bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ventas Totales</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(stats.totalVentas)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-100 text-emerald-600">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 pb-6">
        <div className="rounded-lg shadow-sm border bg-white border-gray-200">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            {/* Filtros y búsqueda */}
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              {/* Filtros de estado */}
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-500">Filtrar:</span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setFiltroEstado('')}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      filtroEstado === ''
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Todos
                  </button>
                  {Object.entries(estadosPedido).map(([key, { label }]) => (
                    <button
                      key={key}
                      onClick={() => setFiltroEstado(key)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        filtroEstado === key
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Selector de rango de fechas */}
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-500" />
                <div className="flex space-x-1">
                  {[
                    { key: 'today' as const, label: 'Hoy' },
                    { key: 'week' as const, label: 'Semana' },
                    { key: 'month' as const, label: 'Mes' },
                    { key: 'custom' as const, label: 'Personalizado' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setDateRange(key)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        dateRange === key
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {dateRange === 'custom' && (
                  <div className="flex items-center gap-2 ml-2">
                    <DatePickerInput
                      value={fechaInicio}
                      onChange={setFechaInicio}
                      placeholder="Desde"
                      className="w-[160px]"
                    />
                    <span className="text-sm text-gray-500">a</span>
                    <DatePickerInput
                      value={fechaFin}
                      onChange={setFechaFin}
                      placeholder="Hasta"
                      className="w-[160px]"
                    />
                  </div>
                )}
              </div>
              
              {/* Búsqueda */}
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {isFetching && searchTerm ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
                  ) : (
                    <Search className="h-5 w-5 text-gray-400" />
                  )}
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Buscar por cliente o número de factura..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm placeholder-gray-500 transition-all focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Contador */}
                <div className="rounded-lg px-4 py-2 bg-emerald-50">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <div className="text-sm">
                      <span className="font-semibold text-emerald-700">
                        {paginationMeta?.total || orders.length}
                      </span>
                      <span className="ml-1 text-gray-600">ventas</span>
                    </div>
                  </div>
                </div>
                
                {/* Selector de cantidad por página */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">Mostrar</span>
                  <div className="relative" data-dropdown>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="border rounded-lg px-3 py-2 pr-8 text-sm font-medium transition-all flex items-center min-w-[60px] bg-white border-gray-300 text-gray-700 hover:border-emerald-300"
                    >
                      {limit}
                    </button>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}" />
                    </div>
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-full border rounded-lg shadow-lg z-10 bg-white border-gray-300">
                        {[7, 10, 20, 50].map((value) => (
                          <div
                            key={value}
                            onClick={() => {
                              handleLimitChange(value);
                              setIsDropdownOpen(false);
                            }}
                            className="px-3 py-2 text-sm font-medium cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg text-emerald-700 hover:bg-emerald-50"
                          >
                            {value}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="relative">
            <OrdersTable 
              orders={orders}
              onOrderClick={handleOrderClick}
              isLoading={isLoading && orders.length === 0}
              mensajesNoLeidos={mensajesNoLeidosPorPedido}
            />
            {isFetching && orders.length > 0 && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center">
                <div className="rounded-lg shadow-lg border px-6 py-4 flex items-center space-x-3 bg-white border-gray-200">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-600 border-t-transparent"></div>
                  <span className="text-sm font-medium text-gray-700">Actualizando...</span>
                </div>
              </div>
            )}
          </div>

          {/* Paginación */}
          {paginationMeta && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
              <div className="flex items-center justify-between">
                {/* Información de resultados */}
                <div className="flex items-center space-x-6">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">
                      {((paginationMeta.page - 1) * paginationMeta.limit) + 1}
                    </span>
                    <span className="mx-1">–</span>
                    <span className="font-medium text-gray-900">
                      {Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total)}
                    </span>
                    <span className="mx-2">de</span>
                    <span className="font-semibold text-gray-900">{paginationMeta.total}</span>
                    <span className="ml-1">resultados</span>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    Página <span className="font-medium text-gray-900">{paginationMeta.page}</span> de <span className="font-medium text-gray-900">{paginationMeta.totalPages}</span>
                  </div>
                </div>
                
                {/* Controles de paginación */}
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={!paginationMeta.hasPrevious}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasPrevious
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-600 cursor-not-allowed'
                    }`}
                    title="Primera página"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>

                  <button
                    onClick={handlePreviousPage}
                    disabled={!paginationMeta.hasPrevious}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasPrevious
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-600 cursor-not-allowed'
                    }`}
                    title="Página anterior"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center space-x-1 mx-2">
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 3;
                      let start = Math.max(1, paginationMeta.page - Math.floor(maxVisiblePages / 2));
                      let end = Math.min(paginationMeta.totalPages, start + maxVisiblePages - 1);
                      
                      start = Math.max(1, end - maxVisiblePages + 1);

                      if (start > 1) {
                        pages.push(
                          <button
                            key="1"
                            onClick={() => handlePageChange(1)}
                            className="w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                          >
                            1
                          </button>
                        );
                        if (start > 2) {
                          pages.push(
                            <span key="ellipsis-start" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                          );
                        }
                      }

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all ${
                              i === paginationMeta.page
                                ? 'bg-emerald-600 text-white'
                                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      if (end < paginationMeta.totalPages) {
                        if (end < paginationMeta.totalPages - 1) {
                          pages.push(
                            <span key="ellipsis-end" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                          );
                        }
                        pages.push(
                          <button
                            key={paginationMeta.totalPages}
                            onClick={() => handlePageChange(paginationMeta.totalPages)}
                            className="w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                          >
                            {paginationMeta.totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={!paginationMeta.hasNext}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasNext
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-600 cursor-not-allowed'
                    }`}
                    title="Página siguiente"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handlePageChange(paginationMeta.totalPages)}
                    disabled={!paginationMeta.hasNext}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasNext
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-600 cursor-not-allowed'
                    }`}
                    title="Última página"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Drawer */}
      <OrderDetailsDrawer
        isOpen={isDrawerOpen}
        pedido={selectedOrder}
        onClose={handleCloseDrawer}
        isAdmin={true}
        onStatusChange={(pedidoId, newStatus) => {
          refetch();
        }}
      />
    </div>
  );
};

export default OrdersPage;
